// eslint-disable-next-line no-restricted-imports
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AxiosResponse } from 'axios';
import { Meta, Null } from 'types/shared';
import validator from 'validator';

import { API } from 'core/api';
import { useCognitoUser } from 'core/cognito';
import { useToast } from 'core/toast/hooks';
import { useHasMobileView } from 'hooks/useHasMobileView';

type TransactionRuleType = 'MERCHANT_CATEGORY_CODES';

interface TransactionRuleGroup {
  id: string;
  name: string;
  description: string;
  type: TransactionRuleType;
  rule: { codes: string[] };
}

interface TransactionRuleGroupsProps {
  page: number;
  limit: number;
}

function useTransactionRuleGroups({ page, limit }: TransactionRuleGroupsProps) {
  const { token } = useCognitoUser();
  const { addToast } = useToast();
  const { hasMobileView } = useHasMobileView();

  interface Response {
    result: Null<TransactionRuleGroup>[];
    meta: Meta;
  }

  interface Params {
    page: number;
    limit: number | undefined;
  }

  function fetchData() {
    return API.get<Response, Params, false>({
      path: '/admin/transaction-rule-groups',
      token,
      params: {
        page,
        limit: hasMobileView && limit === undefined ? 15 : limit,
      },
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<Response>, unknown, Response>(
    ['transactionRuleGroups', { page, limit, hasMobileView }],
    fetchData,
    {
      staleTime: Number.MAX_VALUE,
      enabled: !!token,
      retry: 0,
      select: ({ data }) => data,
    },
  );
}

function useTransactionRuleGroup(id: string) {
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  interface Response {
    result: Null<TransactionRuleGroup>;
    meta: Meta;
  }

  function fetchData() {
    return API.get<Response, void, false>({
      path: `/admin/transaction-rule-groups/${id}`,
      token,
      params: undefined,
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<Response>, unknown, Null<TransactionRuleGroup>>(
    ['transactionRuleGroup', { id }],
    fetchData,
    {
      staleTime: Number.MAX_VALUE,
      enabled: !!token && validator.isUUID(id),
      retry: 0,
      select: ({ data }) => data?.result,
    },
  );
}

function useAddTransactionRuleGroup() {
  const queryClient = useQueryClient();
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  interface AddTransactionRuleGroupProps {
    name: string;
    description: string;
    type: TransactionRuleType;
    rule: { codes: string[] };
  }

  interface Body {
    name: string;
    description: string;
    type: TransactionRuleType;
    rule: { codes: string[] };
  }

  interface Response {
    result: TransactionRuleGroup;
    meta: Meta;
  }

  async function addTransactionRuleGroup(transactionRuleGroup: AddTransactionRuleGroupProps) {
    const { data } = await API.post<Response, Body>({
      path: '/admin/transaction-rule-groups',
      token,
      version: 1,
      body: transactionRuleGroup,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });

    return Promise.resolve(data);
  }

  return useMutation<Response, unknown, AddTransactionRuleGroupProps>(
    (data: AddTransactionRuleGroupProps) => addTransactionRuleGroup(data),
    {
      onSuccess: async (_, id) => {
        await queryClient.invalidateQueries(['transactionRuleGroup', { id }]);
        await queryClient.invalidateQueries(['transactionRuleGroups']);
      },
    },
  );
}

function useUpdateTransactionRuleGroup() {
  const queryClient = useQueryClient();
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  interface UpdateTransactionRuleGroupProps {
    id: string;
    name: string;
    description: string;
    rule: { codes: string[] };
  }

  interface Body {
    id: string;
    name: string;
    description: string;
    rule: { codes: string[] };
  }

  interface Response {
    result: TransactionRuleGroup;
    meta: Meta;
  }

  async function updateTransactionRuleGroup(transactionRuleGroup: UpdateTransactionRuleGroupProps) {
    const { data } = await API.put<Response, Body>({
      path: `/admin/transaction-rule-groups/${transactionRuleGroup.id}`,
      token,
      version: 1,
      body: transactionRuleGroup,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });

    return Promise.resolve(data);
  }

  return useMutation<Response, unknown, UpdateTransactionRuleGroupProps>(
    (data: UpdateTransactionRuleGroupProps) => updateTransactionRuleGroup(data),
    {
      onSuccess: async (_, id) => {
        await queryClient.invalidateQueries(['transactionRuleGroup', { id }]);
        await queryClient.invalidateQueries(['transactionRuleGroups']);
      },
    },
  );
}

function useDeleteTransactionRuleGroup() {
  const queryClient = useQueryClient();
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  async function deleteTransactionRuleGroup(id: string) {
    const { data } = await API.delete<void>({
      path: `/admin/transaction-rule-groups/${id}`,
      token,
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });

    return Promise.resolve(data);
  }

  return useMutation<void, unknown, string>((data) => deleteTransactionRuleGroup(data), {
    onSuccess: async (_) => {
      await queryClient.invalidateQueries(['transactionRuleGroups']);
    },
  });
}

export {
  type TransactionRuleGroup,
  type TransactionRuleType,
  useAddTransactionRuleGroup,
  useDeleteTransactionRuleGroup,
  useTransactionRuleGroup,
  useTransactionRuleGroups,
  useUpdateTransactionRuleGroup,
};
